import { useState } from "react";
import { SITE_INFO } from "@/data/siteData";
import { MapPin, Phone, Mail, Clock, Send, ChevronRight, Users, BookOpen, Building2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ── dati reali dal sito originale ── */
const SEDE_PRINCIPALE = {
  nome: "Roma — Sede Principale",
  indirizzo: "Via Marco e Marcelliano, 45 — 00147 Roma",
  dettaglio: "Scala A, 3° piano",
  telefono: "06.68437500",
  email: "info@confcommercioroma.it",
  pec: "direzione-confcommercioroma@legalmail.it",
  orari: "Lunedì–Giovedì 8:30–17:30 | Venerdì 8:30–13:30",
  mapsUrl:
    "https://www.google.com/maps/search/?api=1&query=Via+Marco+e+Marcelliano+45+00147+Roma",
};

const AREE_MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Via+Marco+e+Marcelliano+45+00147+Roma";

const AREE = [
  {
    id: "servizi",
    titolo: "Area Servizi",
    icon: Users,
    color: "#0052A3",
    bg: "#EBF5FF",
    telefono: "06.68437606 — 530",
    email: "commerciale.promoter@confcommercioroma.it",
    indirizzo: "Via Marco e Marcelliano, 45 — 00147 Roma",
    mapsUrl: AREE_MAPS_URL,
    nota: null,
    linkLabel: "Scopri i nostri Servizi",
    linkHref: "/servizi",
  },
  {
    id: "formazione",
    titolo: "Area Formazione",
    icon: BookOpen,
    color: "#007ACC",
    bg: "#EBF5FF",
    telefono: "06.68437606 – 530 - 462",
    email: "promoter@confcommercioroma.it",
    emailAlt: "areagestione@confcommercioroma.it",
    indirizzo: "Via Marco e Marcelliano, 45 — 00147 Roma (Scala A, 1° piano)",
    mapsUrl: AREE_MAPS_URL,
    nota: "Si riceve solo previo appuntamento. La sede didattica è dotata di parcheggio interno.",
    linkLabel: "Scopri i Percorsi Formativi",
    linkHref: "/formazione",
  },
  {
    id: "promoter",
    titolo: "Promo.Ter",
    icon: Building2,
    color: "#0052A3",
    bg: "#EBF5FF",
    telefono: "06.68437610",
    email: "promoter@confcommercioroma.it",
    pec: "promoterroma@legalmail.it",
    indirizzo: "Via Marco e Marcelliano, 45 — 00147 Roma",
    dettaglio: "Scala A, 2° piano",
    mapsUrl: AREE_MAPS_URL,
    nota: null,
    linkLabel: "Vai a Promo.Ter",
    linkHref: "/chi-siamo/enti-collegati/promo-ter",
  },
  {
    id: "cat",
    titolo: "C.A.T. Confcommercio Roma",
    icon: Building2,
    color: "#0052A3",
    bg: "#EBF5FF",
    telefono: "06.68437369",
    email: "servizi-cat@confcommercioroma.it",
    pec: "catconfcommerciolazio@pec.cat-lazio.it",
    indirizzo: "Via Marco e Marcelliano, 45 — 00147 Roma",
    mapsUrl: AREE_MAPS_URL,
    nota: null,
    linkLabel: "Vai a C.A.T.",
    linkHref: "/chi-siamo/cat",
  },
];

const SEDI_TERRITORIALI = [
  {
    id: "ostia",
    nome: "Ostia",
    indirizzo: "Piazzale della Posta, 2 — 00122 Ostia Lido",
    telefono: "06.5623356",
    email: "ostia@confcommercioroma.it",
    orari: "Lunedì, Martedì, Giovedì 9:00–12:00",
    nota: "Al di fuori di queste fasce orarie l'accesso è possibile solo previo appuntamento.",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Piazzale+della+Posta+2+00122+Ostia+Lido+Roma",
  },
  {
    id: "civitavecchia",
    nome: "Civitavecchia",
    indirizzo: "Corso Guglielmo Marconi, 12 — 00053 Civitavecchia",
    telefono: "06.68437500",
    email: "civitavecchia@confcommercioroma.it",
    orari: "Su appuntamento",
    nota: "Si riceve solo previo appuntamento.",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Corso+Guglielmo+Marconi+12+00053+Civitavecchia+RM",
  },
];

/* ── componenti interni ── */
function InfoRow({ icon: Icon, children, href, testId }) {
  const cls = "flex items-start gap-3 text-sm leading-relaxed";
  return (
    <div className={cls}>
      <Icon size={15} className="shrink-0 mt-0.5 text-[var(--confcom-blue)]" aria-hidden="true" />
      {href ? (
        <a href={href} className="hover:text-[var(--confcom-blue)] transition-colors" data-testid={testId}>
          {children}
        </a>
      ) : (
        <span data-testid={testId}>{children}</span>
      )}
    </div>
  );
}

export default function ContattiPage() {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", oggetto: "", messaggio: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm({ nome: "", email: "", telefono: "", oggetto: "", messaggio: "" });
    } catch {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main id="main-content" data-testid="contatti-page">

      {/* ── Hero ── */}
      <section
        className="relative h-[320px] md:h-[380px] overflow-hidden"
        aria-labelledby="contatti-title"
      >
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=60&auto=format"
          alt="Ufficio Confcommercio Roma"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4,30,66,0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <h1
            id="contatti-title"
            className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight"
          >
            Contatti
          </h1>
          <p className="mt-3 text-white/70 text-base max-w-xl">
            Siamo a disposizione per qualsiasi informazione sui servizi, le attività e le iniziative di Confcommercio Roma.
          </p>
        </div>
      </section>


      {/* ── Sede Principale Roma ── */}
      <section className="py-14 bg-white" aria-labelledby="sede-roma-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              marginBottom: "0.75rem",
            }}
          >
            <Building2 size={18} className="text-[var(--confcom-blue)]" />
            <span
              className="font-['Encode_Sans_Semi_Condensed']"
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "var(--confcom-blue)",
              }}
            >
              Sede Principale
            </span>
          </div>
          <h2
            id="sede-roma-title"
            className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8"
          >
            Roma
          </h2>

          <div
            data-testid="sede-roma-card"
            style={{
              background: "#fff",
              border: "1px solid var(--confcom-border)",
              borderRadius: "1.25rem",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "0",
              }}
            >
              {/* Colonna info */}
              <div style={{ padding: "2.5rem", borderRight: "1px solid var(--confcom-border)" }}>
                <h3
                  className="font-['Encode_Sans_Semi_Condensed']"
                  style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--confcom-text-primary)", marginBottom: "1.5rem" }}
                >
                  {SEDE_PRINCIPALE.nome}
                </h3>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem", color: "var(--confcom-text-secondary)" }}>
                  <InfoRow icon={MapPin} href={SEDE_PRINCIPALE.mapsUrl} testId="sede-roma-indirizzo">
                    {SEDE_PRINCIPALE.indirizzo}
                    <span style={{ display: "block", fontSize: "0.8rem", color: "var(--confcom-text-muted)" }}>
                      {SEDE_PRINCIPALE.dettaglio}
                    </span>
                  </InfoRow>
                  <InfoRow icon={Phone} href={`tel:${SEDE_PRINCIPALE.telefono.replace(/\./g, "")}`} testId="sede-roma-tel">
                    {SEDE_PRINCIPALE.telefono}
                  </InfoRow>
                  <InfoRow icon={Mail} href={`mailto:${SEDE_PRINCIPALE.email}`} testId="sede-roma-email">
                    {SEDE_PRINCIPALE.email}
                  </InfoRow>
                  <InfoRow icon={Mail} href={`mailto:${SEDE_PRINCIPALE.pec}`} testId="sede-roma-pec">
                    <span>
                      <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--confcom-text-muted)", marginRight: "0.3rem" }}>PEC</span>
                      {SEDE_PRINCIPALE.pec}
                    </span>
                  </InfoRow>
                </div>
              </div>

              {/* Colonna orari */}
              <div style={{ padding: "2.5rem", background: "rgba(255,255,255,0.55)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1.25rem" }}>
                  <Clock size={16} className="text-[var(--confcom-blue)]" />
                  <span
                    className="font-['Encode_Sans_Semi_Condensed']"
                    style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: "var(--confcom-text-muted)" }}
                  >
                    Orari di apertura
                  </span>
                </div>
                <p
                  className="font-['Encode_Sans_Semi_Condensed']"
                  style={{ fontSize: "1rem", fontWeight: 600, color: "var(--confcom-text-primary)", lineHeight: 1.5 }}
                  data-testid="sede-roma-orari"
                >
                  {SEDE_PRINCIPALE.orari}
                </p>
                <div
                  style={{
                    marginTop: "2rem",
                    padding: "1rem 1.25rem",
                    background: "var(--confcom-blue)",
                    borderRadius: "0.75rem",
                    color: "#fff",
                    fontSize: "0.82rem",
                  }}
                >
                  <p style={{ fontWeight: 600, marginBottom: "0.3rem" }}>Cerca la tua Associazione</p>
                  <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.78rem", marginBottom: "0.75rem" }}>
                    Trova il contatto dell'Associazione di categoria di riferimento.
                  </p>
                  <a
                    href="/associazioni"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.3rem",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      textDecoration: "none",
                      borderBottom: "1px solid rgba(255,255,255,0.5)",
                      paddingBottom: "1px",
                    }}
                  >
                    Vai alle Associazioni <ChevronRight size={13} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Aree Operative ── */}
      <section className="py-14 bg-[#F5F8FC]" aria-labelledby="aree-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span
            className="font-['Encode_Sans_Semi_Condensed']"
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              color: "var(--confcom-blue)",
              display: "block",
              marginBottom: "0.6rem",
            }}
          >
            Stessa sede — via Marco e Marcelliano 45
          </span>
          <h2
            id="aree-title"
            className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-10"
          >
            Aree Operative
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "1.5rem",
            }}
          >
            {AREE.map((area) => {
              const Icon = area.icon;
              return (
                <div
                  key={area.id}
                  data-testid={`area-${area.id}-card`}
                  style={{
                    background: "#fff",
                    border: "1px solid var(--confcom-border)",
                    borderRadius: "1rem",
                    overflow: "hidden",
                  }}
                >
                  {/* Header colorato */}
                  <div
                    style={{
                      background: area.bg,
                      padding: "1.25rem 1.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "0.5rem",
                        background: area.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={20} color="#fff" />
                    </div>
                    <h3
                      className="font-['Encode_Sans_Semi_Condensed']"
                      style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--confcom-text-primary)" }}
                    >
                      {area.titolo}
                    </h3>
                  </div>

                  {/* Corpo */}
                  <div style={{ padding: "1.5rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", color: "var(--confcom-text-secondary)" }}>
                      <InfoRow icon={Phone} href={`tel:${area.telefono.split("—")[0].trim().replace(/[\s.]/g, "")}`} testId={`area-${area.id}-tel`}>
                        {area.telefono}{area.fax && <span className="text-[var(--confcom-text-muted)]"> | Fax: {area.fax}</span>}
                      </InfoRow>
                      <InfoRow icon={Mail} href={`mailto:${area.email}`} testId={`area-${area.id}-email`}>
                        {area.email}
                      </InfoRow>
                      {area.emailAlt && (
                        <InfoRow icon={Mail} href={`mailto:${area.emailAlt}`} testId={`area-${area.id}-email-alt`}>
                          {area.emailAlt}
                        </InfoRow>
                      )}
                      {area.pec && (
                        <InfoRow icon={Mail} href={`mailto:${area.pec}`} testId={`area-${area.id}-pec`}>
                          <span><span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--confcom-text-muted)", marginRight: "0.3rem" }}>PEC</span>{area.pec}</span>
                        </InfoRow>
                      )}
                      <InfoRow icon={MapPin} href={area.mapsUrl} testId={`area-${area.id}-indirizzo`}>
                        {area.indirizzo}
                        {area.dettaglio && (
                          <span style={{ display: "block", fontSize: "0.8rem", color: "var(--confcom-text-muted)" }}>
                            {area.dettaglio}
                          </span>
                        )}
                      </InfoRow>
                    </div>

                    {area.nota && (
                      <p
                        style={{
                          marginTop: "1rem",
                          padding: "0.75rem",
                          background: "#FFFBEB",
                          border: "1px solid #FDE68A",
                          borderRadius: "0.5rem",
                          fontSize: "0.78rem",
                          color: "#92400E",
                          lineHeight: 1.5,
                        }}
                      >
                        {area.nota}
                      </p>
                    )}

                    <a
                      href={area.linkHref}
                      className="inline-flex items-center gap-1 text-sm font-medium text-[var(--confcom-blue)] hover:gap-2 transition-all"
                      style={{ textDecoration: "none", marginTop: "1.25rem" }}
                    >
                      {area.linkLabel} <ChevronRight size={16} aria-hidden="true" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Sedi Territoriali ── */}
      <section className="py-14 bg-white" aria-labelledby="sedi-territoriali-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span
            className="font-['Encode_Sans_Semi_Condensed']"
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              color: "var(--confcom-blue)",
              display: "block",
              marginBottom: "0.6rem",
            }}
          >
            Presenza sul territorio
          </span>
          <h2
            id="sedi-territoriali-title"
            className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-10"
          >
            Sedi Territoriali
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {SEDI_TERRITORIALI.map((sede) => (
              <div
                key={sede.id}
                data-testid={`sede-${sede.id}-card`}
                style={{
                  background: "#F5F8FC",
                  border: "1px solid var(--confcom-border)",
                  borderRadius: "1rem",
                  padding: "1.75rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    marginBottom: "1.25rem",
                  }}
                >
                  <h3
                    className="font-['Encode_Sans_Semi_Condensed']"
                    style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--confcom-text-primary)" }}
                    data-testid={`sede-${sede.id}-nome`}
                  >
                    {sede.nome}
                  </h3>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", color: "var(--confcom-text-secondary)" }}>
                  <InfoRow icon={MapPin} href={sede.mapsUrl} testId={`sede-${sede.id}-indirizzo`}>
                    {sede.indirizzo}
                  </InfoRow>
                  <InfoRow icon={Phone} href={`tel:${sede.telefono.split("—")[0].trim().replace(/\./g, "")}`} testId={`sede-${sede.id}-tel`}>
                    {sede.telefono}
                  </InfoRow>
                  <InfoRow icon={Mail} href={`mailto:${sede.email}`} testId={`sede-${sede.id}-email`}>
                    {sede.email}
                  </InfoRow>
                  <InfoRow icon={Clock} testId={`sede-${sede.id}-orari`}>
                    {sede.orari}
                  </InfoRow>
                </div>

                {sede.nota && (
                  <p
                    style={{
                      marginTop: "1rem",
                      fontSize: "0.78rem",
                      color: "var(--confcom-text-muted)",
                      fontStyle: "italic",
                      lineHeight: 1.5,
                    }}
                  >
                    {sede.nota}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form Contatto ── */}
      <section className="py-14 bg-[#F5F8FC]" aria-labelledby="form-title">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
            <span
              className="font-['Encode_Sans_Semi_Condensed']"
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: "var(--confcom-blue)",
                display: "block",
                marginBottom: "0.5rem",
              }}
            >
              Scrivici direttamente
            </span>
            <h2
              id="form-title"
              className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']"
            >
              Inviaci un messaggio
            </h2>
          </div>

          <div
            style={{
              background: "#fff",
              borderRadius: "1.25rem",
              padding: "2.5rem",
              border: "1px solid var(--confcom-border)",
              boxShadow: "0 4px 24px rgba(0,52,163,0.06)",
            }}
          >
            <form onSubmit={handleSubmit} data-testid="contact-form" noValidate>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1.25rem",
                  marginBottom: "1.25rem",
                }}
              >
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-[var(--confcom-text-primary)] mb-1.5">
                    Nome e Cognome *
                  </label>
                  <input
                    id="nome"
                    type="text"
                    required
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    className="w-full h-11 px-4 border border-[var(--confcom-border)] rounded-md bg-[var(--confcom-bg-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--confcom-blue)] text-sm"
                    data-testid="contact-nome"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[var(--confcom-text-primary)] mb-1.5">
                    Email *
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full h-11 px-4 border border-[var(--confcom-border)] rounded-md bg-[var(--confcom-bg-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--confcom-blue)] text-sm"
                    data-testid="contact-email"
                  />
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1.25rem",
                  marginBottom: "1.25rem",
                }}
              >
                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-[var(--confcom-text-primary)] mb-1.5">
                    Telefono
                  </label>
                  <input
                    id="telefono"
                    type="tel"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    className="w-full h-11 px-4 border border-[var(--confcom-border)] rounded-md bg-[var(--confcom-bg-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--confcom-blue)] text-sm"
                    data-testid="contact-telefono"
                  />
                </div>
                <div>
                  <label htmlFor="oggetto" className="block text-sm font-medium text-[var(--confcom-text-primary)] mb-1.5">
                    Oggetto *
                  </label>
                  <input
                    id="oggetto"
                    type="text"
                    required
                    value={form.oggetto}
                    onChange={(e) => setForm({ ...form, oggetto: e.target.value })}
                    className="w-full h-11 px-4 border border-[var(--confcom-border)] rounded-md bg-[var(--confcom-bg-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--confcom-blue)] text-sm"
                    data-testid="contact-oggetto"
                  />
                </div>
              </div>

              <div style={{ marginBottom: "1.5rem" }}>
                <label htmlFor="messaggio" className="block text-sm font-medium text-[var(--confcom-text-primary)] mb-1.5">
                  Messaggio *
                </label>
                <textarea
                  id="messaggio"
                  rows={5}
                  required
                  value={form.messaggio}
                  onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
                  className="w-full px-4 py-3 border border-[var(--confcom-border)] rounded-md bg-[var(--confcom-bg-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--confcom-blue)] text-sm resize-none"
                  data-testid="contact-messaggio"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[var(--confcom-blue)] text-white px-7 py-3 rounded-md font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                data-testid="contact-submit"
              >
                <Send size={18} aria-hidden="true" />
                {loading ? "Invio in corso…" : "Invia messaggio"}
              </button>

              {status === "success" && (
                <div
                  className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800"
                  role="alert"
                  data-testid="contact-success"
                  style={{ marginTop: "1rem" }}
                >
                  Messaggio inviato con successo! Ti ricontatteremo al più presto.
                </div>
              )}
              {status === "error" && (
                <div
                  className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800"
                  role="alert"
                  data-testid="contact-error"
                  style={{ marginTop: "1rem" }}
                >
                  Si è verificato un errore. Riprova o contattaci al {SITE_INFO.phone}.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* ── Numero Verde banner rimosso su richiesta utente (entry #0012 CHANGELOG) ── */}
    </main>
  );
}
